import { useCallback, useEffect, useState } from "react";

import { TopBar } from "../../app/Shell";
import { parseRoute } from "../../app/router";
import { isHttpsUrl, type HostAdapter } from "../../shared/host/adapter";
import { applyTheme } from "../../shared/host/theme";
import { useT } from "../../shared/i18n";
import { Button, ErrorState, Skeleton, ToastProvider } from "../../shared/ui";
import { AdminConsole } from "../admin/AdminConsole";
import type { OperationsSnapshot } from "../admin/operations";
import { isOperationsSnapshot } from "./snapshotGuard";

const ADMIN_HOME = "/admin";
const SNAPSHOT_URL = "/operator/operations/snapshot";
const LOGIN_URL = "/operator/auth/login";
const LOGOUT_URL = "/operator/auth/logout";

type Status = "loading" | "ready" | "signin" | "error";

/**
 * The operator console as a browser app (outside Telegram). It is admin-only:
 * no close button, no tab bar and no route back to the user Mini App.
 */
export function StandaloneAdmin({
  host,
  redirect = (url) => {
    window.location.assign(url);
  },
}: {
  host: HostAdapter;
  /** Full-page navigation to the identity provider; replaceable for tests. */
  redirect?: (url: string) => void;
}) {
  const t = useT();
  const [path, setPath] = useState(
    window.location.pathname + window.location.search,
  );
  const [status, setStatus] = useState<Status>("loading");
  const [snapshot, setSnapshot] = useState<OperationsSnapshot | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [loginFailed, setLoginFailed] = useState(false);
  const [theme, setTheme] = useState(() => host.colorScheme());

  useEffect(() => host.onThemeChange(setTheme), [host]);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const onPopState = () => {
      setPath(window.location.pathname + window.location.search);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  // Nothing outside the admin area is reachable in this mode.
  const insideAdmin = parseRoute(path).kind === "admin";
  useEffect(() => {
    if (insideAdmin) return;
    window.history.replaceState({}, "", ADMIN_HOME);
  }, [insideAdmin]);

  useEffect(() => {
    let active = true;
    void fetch(SNAPSHOT_URL, { credentials: "include" })
      .then(async (response) => {
        if (response.status === 401 || response.status === 403) {
          if (active) setStatus("signin");
          return;
        }
        if (!response.ok)
          throw new Error(`operator_http_${String(response.status)}`);
        const value: unknown = await response.json();
        if (!isOperationsSnapshot(value))
          throw new Error("operator_snapshot_invalid");
        if (active) {
          setSnapshot(value);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  const navigate = useCallback(
    (href: string, options?: { replace?: boolean }) => {
      if (parseRoute(href).kind !== "admin") return;
      if (options?.replace) window.history.replaceState({}, "", href);
      else window.history.pushState({}, "", href);
      setPath(href);
    },
    [],
  );

  const backToSignIn = useCallback(() => {
    setSnapshot(null);
    setLoginFailed(false);
    setStatus("signin");
  }, []);

  const startLogin = async () => {
    setLoginFailed(false);
    try {
      const response = await fetch(LOGIN_URL, { credentials: "include" });
      if (!response.ok) throw new Error("login_http");
      const body: unknown = await response.json();
      if (
        typeof body !== "object" ||
        body === null ||
        !("authorization_url" in body) ||
        typeof body.authorization_url !== "string" ||
        !isHttpsUrl(body.authorization_url)
      )
        throw new Error("login_invalid");
      redirect(body.authorization_url);
    } catch {
      setLoginFailed(true);
    }
  };

  const signOut = async () => {
    try {
      const response = await fetch(LOGOUT_URL, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error("logout_http");
      backToSignIn();
    } catch {
      setStatus("error");
    }
  };

  const signOutButton =
    status === "ready" ? (
      <Button
        variant="tinted"
        size="sm"
        aria-label={t("admin.standalone.signOutAria")}
        onClick={() => {
          void signOut();
        }}
      >
        {t("admin.standalone.signOut")}
      </Button>
    ) : undefined;

  return (
    <ToastProvider closeLabel={t("app.close")}>
      <div className="min-h-screen bg-canvas text-fg">
        <TopBar eyebrow={t("app.administration")} trailing={signOutButton} />
        {status === "ready" && snapshot ? (
          <AdminConsole
            snapshot={snapshot}
            path={insideAdmin ? path : ADMIN_HOME}
            onNavigate={navigate}
            onSignIn={backToSignIn}
          />
        ) : status === "loading" ? (
          <main className="p-gutter" aria-busy="true">
            <Skeleton height={160} />
          </main>
        ) : status === "signin" ? (
          <ErrorState
            title={t("admin.title")}
            body={
              loginFailed
                ? t("admin.loadFailed")
                : `${t("admin.standalone.prompt")} ${t("admin.standalone.hint")}`
            }
            action={
              <Button
                onClick={() => {
                  void startLogin();
                }}
              >
                {t("admin.signIn")}
              </Button>
            }
          />
        ) : (
          <ErrorState
            title={t("admin.title")}
            body={t("admin.loadFailed")}
            action={
              <Button
                onClick={() => {
                  setStatus("loading");
                  setAttempt((value) => value + 1);
                }}
              >
                {t("error.retry")}
              </Button>
            }
          />
        )}
      </div>
    </ToastProvider>
  );
}
